import React, { useCallback, useContext, useState } from 'react';

import { useRouter } from 'next/router';

import { useLDFlags } from '../../utils/LD/useLDFlags';

type NumberContext = {
  pageNum: number;
  setPageNum: (pageNum: number) => void;
};

const PageNumContext = React.createContext<NumberContext>({
  pageNum: 1,
  setPageNum: () => {},
});

export const useReplacePageNumURL = () => {
  const { asPath, query, replace, pathname } = useRouter();
  const newAsPath = useCallback(
    page => {
      const [currentUrl] = asPath.split('#', 1);
      const [currentPathname, currentParams] = currentUrl.split('?');
      const newParams = new URLSearchParams(currentParams);
      const key = 'pageNum';

      if (!page) {
        newParams.delete(key);
      } else {
        newParams.set(key, `${page}`);
      }
      const newQueryString = newParams.toString();
      return newQueryString
        ? `${currentPathname}?${newQueryString}`
        : `${currentPathname}`;
    },
    [asPath]
  );

  const replaceURL = async page => {
    // 'next/router' doesn't guarantee that the router state (pathname, query)
    // is updated between calls to `router.replace`, even if `await` is used.
    // Ex.
    // `useGridFilterBarUtils` may update the URL with a newly selected filter
    // and then call `setPageNum`, which ends up calling this `replaceURL`,
    // but at this point, the changes in the URL made by
    // `useGridFilterBarUtils` may not yet be reflected in `router.query`.
    //
    // That's why the URL is directly read from `window.location` instead,
    // as that's up-to-date.
    //
    // Note that this only solves the problem with previous URL updates in the
    // case where `setPageNum` is called with the value 1 and the URL already
    // has the query param `pageNum=1`. Other cases are currently not supported.
    //
    const queryPage =
      Number(new URLSearchParams(window.location.search).get('pageNum')) || 1;
    if (page === 1 && queryPage === 1) {
      return;
    }

    const asPath = newAsPath(page);
    await replace(
      {
        pathname,
        query: { ...query, pageNum: page },
      },
      asPath,
      { shallow: true }
    );
  };

  return replaceURL;
};

export const GridPageNumProvider = ({ children }) => {
  const { 'numbered-pagination': gridPaginationEnabled } = useLDFlags();
  const { query } = useRouter();
  const [pageNum, setPageNum] = useState(Number(query?.pageNum || 1));
  const replaceURL = useReplacePageNumURL();
  const setPageNumHandler = useCallback(
    async page => {
      await replaceURL(page);
      setPageNum(page);
    },
    [setPageNum, replaceURL]
  );

  if (gridPaginationEnabled) {
    return (
      <PageNumContext.Provider
        value={{ pageNum, setPageNum: setPageNumHandler }}
      >
        {children}
      </PageNumContext.Provider>
    );
  }

  return children;
};

export const usePageNum = () => {
  return useContext(PageNumContext);
};
